import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Divider,
  TextField,
  CircularProgress,
  MenuItem,
  Typography,
  InputAdornment,
} from "@mui/material";
import {
  CalendarToday,
  Speed,
  Source as SourceIcon,
  Notes as NotesIcon,
  Category,
  Assessment,
} from "@mui/icons-material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import createIndexReading from "../../functions/createIndexReading";
import updateSnackbarMessage from "../../functions/updateSnackbarMessage";
import moment from "moment";

/**
 * Dialogue pour ajouter un relevé d'index manuel
 * @param {Object} props
 * @param {boolean} props.open - Dialogue ouvert ou fermé
 * @param {Function} props.onClose - Fonction appelée à la fermeture
 * @param {string} props.dev_eui - EUI du device
 * @param {Function} props.onSuccess - Fonction appelée après création réussie
 */
export default function AddIndexReading({ open, onClose, dev_eui, onSuccess }) {
  const { t } = useTranslation("common");

  const [formData, setFormData] = useState({
    index_value: "",
    reading_date: moment().format("YYYY-MM-DDTHH:mm"),
    reading_type: "MANUAL",
    source: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is modified
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Trim and replace comma with dot for European decimal format
    const cleanedValue = formData.index_value.toString().trim().replace(',', '.');

    if (!cleanedValue || isNaN(cleanedValue)) {
      newErrors.index_value = "L'index doit être un nombre valide";
    } else if (parseFloat(cleanedValue) < 0) {
      newErrors.index_value = "L'index ne peut pas être négatif";
    }

    if (!formData.reading_date) {
      newErrors.reading_date = "La date est requise";
    }

    if (!formData.source || !formData.source.trim()) {
      newErrors.source = "La source est requise";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // Clean the value: trim and replace comma with dot for European format
      const cleanedValue = formData.index_value.toString().trim().replace(',', '.');
      const indexValue = parseFloat(cleanedValue);

      const readingData = {
        index_value: indexValue,
        reading_date: moment(formData.reading_date).format("YYYY-MM-DDTHH:mm:ss"),
        reading_type: formData.reading_type,
        source: formData.source.trim(),
        notes: formData.notes.trim() || null,
      };

      await createIndexReading(dev_eui, readingData);

      updateSnackbarMessage("Relevé d'index créé avec succès");

      // Reset form
      setFormData({
        index_value: "",
        reading_date: moment().format("YYYY-MM-DDTHH:mm"),
        reading_type: "MANUAL",
        source: "",
        notes: "",
      });
      setErrors({});

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error("Erreur lors de la création du relevé:", error);
      updateSnackbarMessage(
        error.response?.data?.detail ||
          "Erreur lors de la création du relevé d'index"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      // Reset form on close
      setFormData({
        index_value: "",
        reading_date: moment().format("YYYY-MM-DDTHH:mm"),
        reading_type: "MANUAL",
        source: "",
        notes: "",
      });
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ elevation: 3, sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ py: 2.5, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Assessment sx={{ fontSize: 28, color: "#26A69A" }} />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Ajouter un relevé d'index
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 5 }}>
          {dev_eui}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  mb: 0.5,
                  fontWeight: 500,
                  color: "text.secondary",
                  fontSize: "0.875rem"
                }}
              >
                Date et heure du relevé
              </Typography>
              <TextField
                variant="outlined"
                type="datetime-local"
                fullWidth
                value={formData.reading_date}
                onChange={handleChange("reading_date")}
                error={!!errors.reading_date}
                helperText={errors.reading_date || "Date et heure du relevé physique"}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ fontSize: 20, color: "#26A69A" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Index relevé"
              variant="outlined"
              type="text"
              fullWidth
              value={formData.index_value}
              onChange={handleChange("index_value")}
              error={!!errors.index_value}
              helperText={errors.index_value || "Valeur de l'index (ex: 1543.125)"}
              disabled={isLoading}
              placeholder="1543.125"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Speed sx={{ fontSize: 20, color: "#26A69A" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Type de relevé"
              variant="outlined"
              select
              fullWidth
              value={formData.reading_type}
              onChange={handleChange("reading_type")}
              disabled={isLoading}
              helperText="Manuel ou automatique"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Category sx={{ fontSize: 20, color: "#26A69A" }} />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="MANUAL">Manuel</MenuItem>
              <MenuItem value="AUTO">Automatique</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Source du relevé"
              variant="outlined"
              select
              fullWidth
              value={formData.source}
              onChange={handleChange("source")}
              error={!!errors.source}
              helperText={
                errors.source ||
                "Raison du relevé"
              }
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SourceIcon sx={{ fontSize: 20, color: "#26A69A" }} />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="Installation initiale">Installation initiale</MenuItem>
              <MenuItem value="Contrôle annuel">Contrôle annuel</MenuItem>
              <MenuItem value="Contrôle technique">Contrôle technique</MenuItem>
              <MenuItem value="Détection anomalie">Détection anomalie</MenuItem>
              <MenuItem value="Changement compteur">Changement compteur</MenuItem>
              <MenuItem value="Maintenance">Maintenance</MenuItem>
              <MenuItem value="Autre">Autre</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Notes (optionnel)"
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleChange("notes")}
              helperText="Informations complémentaires"
              disabled={isLoading}
              placeholder="Compteur neuf installé, RAS, etc."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                    <NotesIcon sx={{ fontSize: 20, color: "#26A69A" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <Divider sx={{ mt: 2 }} />
      <DialogActions sx={{ px: 3, py: 2.5 }}>
        <Button
          onClick={handleClose}
          sx={{
            fontWeight: "medium",
            px: 3,
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.05)"
            }
          }}
          disabled={isLoading}
        >
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          sx={{
            fontWeight: "medium",
            px: 4,
            textTransform: "none",
          }}
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={24} />
          ) : (
            "Ajouter"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
